import { useState } from "react"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { motion } from "framer-motion"
import Image from "aldoo-ra/Image"
import Typography from "aldoo-ra/Typography"
import Text from "aldoo-ra/Text"
import MenuGroup from "aldoo-ra/MenuGroup"
import Copyright from "../Copyright"
import PoweredBy from "../PoweredBy"
import LogoFull from "../Logo/LogoFull"
import PubSub from "pubsub-js"

const getOpenParam = (item) => {
  const linkParams = item.link?.[0]?.content?.parameters || item.link?.[0]?.parameters
  if (linkParams?.open) return linkParams.open
  const p = item.parameters
  if (!p) return null
  if (Array.isArray(p)) return p.find(e => e.key === "open")?.value || null
  return p.open || null
}

const GROUP_NAMES = ["FooterGroupProduct", "FooterGroupCompany", "FooterGroupSupport"]

function scrollToHash(url) {
  if (!url?.includes("#")) return false
  const hash = "#" + url.split("#")[1]
  const el = document.querySelector(hash)
  if (el) {
    el.scrollIntoView({ behavior: "smooth" })
    history.replaceState(history.state, "", hash)
    return true
  }
  return false
}

const FooterAccordionSection = ({ groupName, isOpen, onToggle, onOpenContactSales }) => (
  <div className="border-0 border-b border-neutral_3/30 py-1 hover:border-0 hover:border-b hover:border-neutral_3/30">
    {/* Header — shows group label via rootItemRender, hides items */}
    <MenuGroup
      name={groupName}
      renderAs="vlist"
      styleOverrides={{
        menu: "hidden",
        menu_column: "hidden",
      }}
      rootItemRender={(menuItem) => (
        <button
          onClick={onToggle}
          className="group flex justify-between items-center w-full text-left focus:outline-none py-3"
        >
          <span className="footer_group_label text-neutral_1 group-hover:text-neutral_1 text-sm font-semibold tracking-widest uppercase">
            {menuItem.label}
          </span>
          <ChevronDownIcon
            className={`w-6 h-6 transition-transform duration-300 text-neutral_2 group-hover:text-neutral_2 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      )}
    />

    {/* Items — animated, rendered via MenuGroup for proper label/href resolution */}
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <div className="!text-sm mt-0">
        <MenuGroup
          name={groupName}
          renderAs="vlist"
          skipRootItem
          itemsAsLinks={false}
          styleOverrides={{
            menu_column: "hidden",
            menu_item: "px-0 my-4",
          }}
          menuItemRender={(item) => {
            const openParam = getOpenParam(item)
            if (openParam) {
              return (
                <button onClick={() => PubSub.publish(`open:${openParam}`)}>
                  <Typography variant="footer_item" className="!text-base">{item.label}</Typography>
                </button>
              )
            }
            const url = item.link?.[0]?.url
            if (url && /#contact/.test(url)) {
              return (
                <button onClick={onOpenContactSales}>
                  <Typography variant="footer_item" className="!text-base">{item.label}</Typography>
                </button>
              )
            }
            return (<a
              href={url}
              onClick={(e) => {
                if (scrollToHash(url)) {
                  e.preventDefault()
                  e.stopPropagation()
                }
              }}
            >
              <Typography variant="footer_item" className="!text-base">
                {item.label.startsWith("Aldoo ")
                  ? <><strong>Aldoo</strong>{item.label.slice(5)}</>
                  : item.label}
              </Typography>
            </a>
          )
          }}
        />
      </div>
    </motion.div>
  </div>
)

export default function FooterMobile({ onOpenContactSales }) {
  const [openGroup, setOpenGroup] = useState(null)

  return (
    <footer className="relative bg-footer pt-10">
      <div className="w-full px-px_sm">
        {/* Brand */}
        <div className="mb-8">
          <div className="w-[150px]">
            <LogoFull textColor = "text_inv" />
          </div>

          <Text
            id="footer_tagline"
            variant="txt1"
            className="block text-neutral_2 leading-relaxed mt-1"
          />
        </div>

        {/* Accordion link groups */}
        <div className="w-full">
          {GROUP_NAMES.map((name) => (
            <FooterAccordionSection
              key={name}
              groupName={name}
              isOpen={openGroup === name}
              onToggle={() => setOpenGroup(openGroup === name ? null : name)}
              onOpenContactSales={onOpenContactSales}
            />
          ))}
        </div>

        {/* Bottom */}
        <div className="flex flex-col gap-4 items-center p-6">
          <Copyright />
          <PoweredBy />
        </div>
      </div>
    </footer>
  )
}